"use client";

import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { AlertCircleIcon, FileTextIcon, LoaderIcon, SearchIcon } from "lucide-react";
import { hostLabel } from "@/lib/citations";
import { cn } from "@/lib/utils";

type SearchArgs = { query?: string; url?: string };
type SearchResult =
  | { results: { n: number; url: string; title: string }[] }
  | { n: number; url: string; title: string }
  | { error: string; message: string };

const ERROR_LABELS: Record<string, string> = {
  rate_limited: "Search is busy, retried without it",
  timeout: "Search timed out",
  blocked_url: "That address can't be read",
  unsupported_content: "That page isn't text",
};

/**
 * A one-line trace of a web_search or fetch_page call ("Searched the web for …, 6
 * results"). The sources themselves open from a button under the answer (MessageSources).
 */
export const WebSearchToolUI: ToolCallMessagePartComponent = ({ toolName, ...part }) => {
  const args = part.args as SearchArgs;
  const result = part.result as SearchResult | undefined;
  const { status } = part;
  const running = status.type === "running";
  const isFetch = toolName === "fetch_page";
  const failed = result && "error" in result;

  let label: string;
  if (isFetch) {
    const host = args.url ? hostLabel(args.url) : "a page";
    label = running ? `Reading ${host}` : `Read ${host}`;
  } else {
    const q = args.query ? `“${args.query}”` : "the web";
    label = running ? `Searching ${q}` : `Searched ${q}`;
  }

  let detail: string | null = null;
  if (failed) detail = ERROR_LABELS[result.error] ?? "Search failed";
  else if (result && "results" in result) {
    const count = result.results.length;
    detail = count === 0 ? "no results" : `${count} result${count === 1 ? "" : "s"}`;
  }

  const Icon = running
    ? LoaderIcon
    : failed
      ? AlertCircleIcon
      : isFetch
        ? FileTextIcon
        : SearchIcon;

  return (
    <div
      data-slot="web-search-tool"
      className="text-muted-foreground flex min-w-0 items-center gap-2 py-1 text-sm"
    >
      <Icon
        aria-hidden
        className={cn("size-4 shrink-0", running && "animate-spin [animation-duration:0.6s]")}
      />
      <span className="min-w-0 truncate">{label}</span>
      {detail && <span className="shrink-0 text-xs opacity-70">· {detail}</span>}
    </div>
  );
};
